import React from 'react';
import MainLayout from '../components/layout/MainLayout';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import SEO from '../components/common/SEO';
import FrameworkPillar from '../components/framework/FrameworkPillar';
import FrameworkOS from '../components/framework/FrameworkOS';
import { motion } from 'framer-motion';

const Framework = () => {
  const clarityLink = "https://tidycal.com/1x8odpm/ai-clarity-session-1y6rlrd-mpnrkpl";
  
  const pillars = [
    { 
      letter: 'S', title: 'Secure', statement: 'Protect the trust your organisation depends on.',
      why: 'AI is only valuable if staff, volunteers, partners and communities trust how it is being used. Security inside a mission-led organisation is a moral layer, not just a technical one.',
      examples: ['Protect sensitive information', 'Understand what data is shared', 'Choose appropriate tools', 'Safeguard people'],
      reflection: 'How do we ensure AI strengthens trust rather than putting it at risk?'
    },
    { 
      letter: 'A', title: 'Accountable', statement: 'Make responsibility visible.',
      why: 'AI should never become "everyone\'s responsibility" and therefore nobody\'s responsibility. Effective leadership requires clear ownership and transparent decision-making.',
      examples: ['Clear ownership', 'Leadership oversight', 'Review processes', 'Transparent decision making'],
      reflection: 'Who is responsible for AI decisions across our organisation?'
    },
    { 
      letter: 'F', title: 'Future Ready', statement: 'Build systems that evolve with confidence.',
      why: 'AI changes quickly. Organisations need leadership habits that adapt rather than constantly chasing new technology. Readiness is about building foundations for the long term.',
      examples: ['Regular review', 'Continuous learning', 'Flexible guidance', 'Strategic planning'],
      reflection: 'Are we building capability for the future rather than reacting to every new AI tool?'
    },
    { 
      letter: 'E', title: 'Empowered', statement: 'Help people feel confident using AI.',
      why: 'Leading AI well depends on people. Training, confidence and curiosity matter more than simply providing access to new technology.',
      examples: ['Staff learning', 'Practical guidance', 'Safe experimentation', 'Shared learning'],
      reflection: 'Does our team feel confident using AI responsibly?'
    },
    { 
      letter: 'A', title: 'Aligned', statement: 'Keep AI connected to your mission.',
      why: 'Every AI decision should support the organisation\'s purpose rather than becoming another technology project. AI should amplify your impact, not drift your focus.',
      examples: ['Strategic priorities', 'Mission alignment', 'Organisational values', 'Community outcomes'],
      reflection: 'How does AI help us achieve our mission more effectively?'
    },
    { 
      letter: 'I', title: 'Inclusive', statement: 'Design AI for everyone.',
      why: 'Leading AI responsibly requires considering accessibility, bias, diversity and the unique needs of the communities your organisation serves.',
      examples: ['Inclusive design', 'Bias awareness', 'Accessibility', 'Community voice'],
      reflection: 'Who benefits from our use of AI, and who might unintentionally be excluded?'
    }
  ];

  return (
    <MainLayout>
      <SEO 
        title="The SAFE AI Framework | Leading AI with Confidence" 
        description="A practical, board-ready leadership methodology designed specifically for social impact organisations to lead AI responsibly while protecting people and values." 
        canonical="/safe-ai-framework" 
      />
      
      {/* Hero Section */}
      <Section bg="cream" className="pt-24 pb-20">
        <div className="max-w-5xl mx-auto text-center">
          <motion.span 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-brand-teal font-black text-sm uppercase tracking-[0.3em] mb-6 block"
          >
            Leadership before technology
          </motion.span>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-brand-navy mb-8 leading-tight tracking-tight">
            Leading AI well is not simply about choosing the right tools.
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed max-w-3xl mx-auto font-medium">
            It is about making thoughtful leadership decisions that strengthen your organisation, protect your mission and give your team the confidence to use AI well.
          </p>
        </div>
      </Section>

      {/* Why Section */}
      <Section bg="white" className="py-24 border-y border-gray-100">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-16 items-center">
          <div className="w-full lg:w-1/2">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-8 leading-tight">Why we created the SAFE AI Framework</h2>
            <div className="space-y-6 text-gray-700 text-lg leading-relaxed">
              <p>Many organisations are already using AI. Staff are experimenting, volunteers are trying new tools, but leadership guidance often comes later.</p>
              <p>Without clear intent, AI develops inconsistently. The SAFE AI Framework was created to help leadership teams move from reactive adoption to intentional leadership.</p>
              <p className="font-bold text-brand-navy">It provides a shared language for making decisions, building confidence and embedding AI in ways that support long-term organisational success.</p>
            </div>
          </div>
          <div className="w-full lg:w-1/2">
            <img 
              src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=1200" 
              alt="Strategic Leadership Workshop" 
              className="rounded-[3rem] shadow-2xl"
            />
          </div>
        </div>
      </Section>

      {/* Pillars Intro */}
      <Section bg="cream" className="py-24">
        <div className="max-w-4xl mx-auto text-center mb-16 px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-6">Six leadership principles for confident AI adoption</h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Each principle supports a different part of successful AI leadership. Together they help organisations make practical decisions that protect people while increasing organisational capacity.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {pillars.map((pillar, i) => (
            <FrameworkPillar key={i} index={i} {...pillar} />
          ))}
        </div>
      </Section>

      {/* Framework in Practice */}
      <Section bg="white" className="py-24 border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-16 items-start">
            <div className="w-full lg:w-1/2">
              <h2 className="text-3xl md:text-4xl font-bold text-brand-navy mb-8">The SAFE AI Framework in practice</h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                The framework isn't a static document—it is the operating system we use across all our services to ensure a consistent leadership approach regardless of where your organisation begins.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {["AI Leadership Health Check", "AI Leadership Review", "AI Confidence Training", "AI Leadership Sprint"].map((svc, i) => (
                  <div key={i} className="bg-brand-cream p-4 rounded-xl font-bold text-brand-navy text-sm border border-brand-teal/10">
                    {svc}
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full lg:w-1/2">
              <FrameworkOS />
            </div>
          </div>
        </div>
      </Section>

      {/* Benefits Section */}
      <Section bg="navy" className="py-24">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">What leaders gain</h2>
          <p className="text-xl text-gray-300">Sustainable outcomes driven by human-centred leadership.</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {[
            "Greater leadership confidence",
            "Clear organisational direction",
            "Consistent AI practices",
            "Increased team capability",
            "Protected trust and reputation",
            "Sustainable AI leadership"
          ].map((benefit, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-white/5 border border-white/10 p-8 rounded-[2rem] hover:bg-white/10 transition-colors flex items-center gap-4"
            >
              <div className="w-10 h-10 bg-brand-teal rounded-xl flex items-center justify-center shrink-0">
                <span className="text-white font-bold">{i+1}</span>
              </div>
              <span className="font-bold text-white text-lg leading-tight">{benefit}</span>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Closing Section */}
      <Section bg="white" className="py-24 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold text-brand-navy mb-8">Ready to lead AI with confidence?</h2>
          <p className="text-xl text-gray-600 mb-12 leading-relaxed">
            Every organisation begins in a different place. Whether you're exploring AI for the first time or embedding it across your organisation, we'll help you apply the SAFE AI Framework in a way that fits your people, mission and goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button to="/assessment" variant="primary" className="text-lg px-8 py-4 shadow-sm">
              Take the AI Leadership Health Check
            </Button>
            <Button href={clarityLink} variant="outline" className="text-lg px-8 py-4 bg-white border-2">
              Book your AI Leadership Review
            </Button>
          </div>
        </div>
      </Section>
    </MainLayout>
  );
};

export default Framework;